import * as React from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { ButtonBase, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { mediaQuery } from '../../theme';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useStyles } from './styles';
import { StatusChip } from '../ui';
import { orderStatus } from '../../lib/orderStatus';
import { formatAmount, formatDate } from '../../lib/formatDate';

interface OrdersTableProps {
    orders: any[];
    paging: { page: number; perPage: number; total: number; [key: string]: any };
    pagingUpdate: (params: any) => void;
}

const ROWS_PER_PAGE = [25, 50, 100];

const detailsPath = (order: any) => `/${order.clientId}/orders/${order.orderId}/details`;

/** Row click is a mouse convenience only: it must not steal a text selection or a click on a link/button inside the row. */
const isPlainRowClick = (event: React.MouseEvent<HTMLElement>): boolean => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a, button, input, [role="button"]')) return false;
    return !(window.getSelection?.()?.toString());
};

/** Orders list: a dense table on desktop, one card per order under 768px. */
export default function OrdersTable({ orders, paging, pagingUpdate }: OrdersTableProps) {
    const classes = useStyles();
    const navigate = useNavigate();
    const theme = useTheme();
    const phone = useMediaQuery(mediaQuery(theme.custom.mq.sm));

    const handleChangePage = (_event: unknown, newPage: number) => {
        pagingUpdate({ ...paging, page: newPage + 1, perPage: paging.perPage });
    };
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        pagingUpdate({ ...paging, page: 1, perPage: +event.target.value });
    };

    const pagination = (
        <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE}
            component="div"
            count={paging.total}
            rowsPerPage={paging.perPage}
            page={Math.max(0, paging.page - 1)}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={phone ? 'Rows' : 'Rows per page'}
        />
    );

    if (phone) {
        return (
            <>
                <div className={classes.cards}>
                    {orders.map((order) => {
                        const status = orderStatus(order.orderStatusId);
                        return (
                            <ButtonBase
                                key={order.orderId}
                                component="div"
                                className={classes.card}
                                onClick={() => navigate(detailsPath(order))}
                            >
                                <div className={classes.cardTop}>
                                    <div>
                                        <div className={classes.orderNumber}>#{order.orderNumber}</div>
                                        <div className={classes.orderExternal}>{order.externalOrderId || '—'}</div>
                                    </div>
                                    <StatusChip tone={status.tone}>{status.label}</StatusChip>
                                </div>
                                <div className={classes.cardGrid}>
                                    <div><span className={classes.cardLabel}>Created</span><span className={classes.cardValue}>{formatDate(order.createdAt)}</span></div>
                                    <div><span className={classes.cardLabel}>Updated</span><span className={classes.cardValue}>{formatDate(order.updatedAt)}</span></div>
                                    <div><span className={classes.cardLabel}>Items</span><span className={classes.cardValue}>{order.orderItemQuantity ?? '—'}</span></div>
                                    <div><span className={classes.cardLabel}>Total with shipping</span><span className={classes.cardValue}>{formatAmount(order.totalOrderPriceWithShipment)}</span></div>
                                </div>
                                <div className={classes.cardChips}>
                                    <StatusChip size="sm" dot tone={order.shipstationCreated ? 'success' : 'neutral'}>
                                        {order.shipstationCreated ? 'In ShipStation' : 'Not in ShipStation'}
                                    </StatusChip>
                                    {order.onHold ? <StatusChip size="sm" dot tone="warning">On hold</StatusChip> : null}
                                </div>
                            </ButtonBase>
                        );
                    })}
                </div>
                {pagination}
            </>
        );
    }

    return (
        <>
            <TableContainer className={classes.container}>
                <Table stickyHeader aria-label="Orders">
                    <TableHead>
                        <TableRow>
                            <TableCell>Order</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Updated</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>ShipStation</TableCell>
                            <TableCell>Hold</TableCell>
                            <TableCell align="right">Items</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">With shipping</TableCell>
                            <TableCell padding="checkbox" aria-label="Open" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {orders.map((order) => {
                            const status = orderStatus(order.orderStatusId);
                            const path = detailsPath(order);
                            return (
                                <TableRow
                                    hover
                                    key={order.orderId}
                                    className={classes.row}
                                    onClick={(event) => {
                                        if (isPlainRowClick(event)) navigate(path);
                                    }}
                                >
                                    <TableCell className={classes.orderCell}>
                                        <Link to={path} className={classes.orderLink}>#{order.orderNumber}</Link>
                                        <div className={classes.orderExternal}>{order.externalOrderId || '—'}</div>
                                    </TableCell>
                                    <TableCell className={classes.dateCell}>{formatDate(order.createdAt)}</TableCell>
                                    <TableCell className={classes.dateCell}>{formatDate(order.updatedAt)}</TableCell>
                                    <TableCell><StatusChip tone={status.tone}>{status.label}</StatusChip></TableCell>
                                    <TableCell>
                                        <StatusChip size="sm" dot tone={order.shipstationCreated ? 'success' : 'neutral'}>
                                            {order.shipstationCreated ? 'Yes' : 'No'}
                                        </StatusChip>
                                    </TableCell>
                                    <TableCell>
                                        {order.onHold ? <StatusChip size="sm" dot tone="warning">On hold</StatusChip> : <span className={classes.muted}>—</span>}
                                    </TableCell>
                                    <TableCell className={classes.numeric}>{order.orderItemQuantity ?? '—'}</TableCell>
                                    <TableCell className={classes.numeric}>{formatAmount(order.totalOrderPrice)}</TableCell>
                                    <TableCell className={classes.numeric}>{formatAmount(order.totalOrderPriceWithShipment)}</TableCell>
                                    <TableCell padding="checkbox">
                                        <Tooltip title="Open order">
                                            <IconButton
                                                size="small"
                                                className={classes.openButton}
                                                aria-label={`Open order ${order.orderNumber}`}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    navigate(path);
                                                }}
                                            >
                                                <ChevronRightIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {pagination}
        </>
    );
}
